import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { barsApi } from '@/api/endpoints/bars';
import { SpecialCard } from '@/components/domain';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageSpinner,
  Button,
} from '@/components/ui';
import { ArrowLeft, Plus, Beer, Utensils } from 'lucide-react';

export function SpecialsManagePage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['bar', id],
    queryFn: () => barsApi.getById(id!),
    enabled: !!id,
  });

  const bar = data?.bar;
  const specials = bar?.specials || [];

  if (isLoading) return <PageSpinner />;

  if (!bar) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--color-muted)]">Bar not found</p>
      </div>
    );
  }

  const drinkSpecials = specials.filter((s) => s.type === 'drink');
  const foodSpecials = specials.filter((s) => s.type === 'food');

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to={`/admin/bars/${id}`}
        className="flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to {bar.name}
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Manage Specials</h1>
          <p className="mt-1 text-[var(--color-muted)]">{bar.name}</p>
        </div>
        <Button disabled>
          <Plus className="mr-2 h-4 w-4" />
          Add Special
        </Button>
      </div>

      {/* Coming soon notice */}
      <Card>
        <CardContent className="p-4 text-center text-sm text-[var(--color-muted)]">
          Full CRUD for specials coming soon. For now, specials are managed via AI extraction.
        </CardContent>
      </Card>

      {/* Drink Specials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Beer className="h-5 w-5" />
            Drink Specials ({drinkSpecials.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {drinkSpecials.length === 0 ? (
            <p className="text-center text-[var(--color-muted)]">No drink specials</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {drinkSpecials.map((special) => (
                <SpecialCard key={special.id} special={special} showBar={false} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Food Specials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Utensils className="h-5 w-5" />
            Food Specials ({foodSpecials.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {foodSpecials.length === 0 ? (
            <p className="text-center text-[var(--color-muted)]">No food specials</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {foodSpecials.map((special) => (
                <SpecialCard key={special.id} special={special} showBar={false} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
